import { createStore, createEffect, combine } from "effector"

import ApiService from "services/ApiService"
import { ApiLinkEntity, ApiLinkRel, ApiServiceName, FilterCategory } from "config/constants"
import { IApiLink, IAppliedFilter } from "@/types"
import { IGetProfitCentersDto, ProfitCenters } from "models/ProfitCenters"
import ErrorHandler from "utils/errorHandler"
import CustomError from "models/CustomError"
import { ICreateProfitCenterDto } from "models/ProfitCenter"

// Stores
export const $tenantProfitCenters = createStore<ProfitCenters | null>(null)
export const $userProfitCenters = createStore<ProfitCenters | null>(null)
export const $userProfitCenterIds = combine($userProfitCenters, userProfitCenters => {
    return userProfitCenters?.items.map(pc => pc.id)
})

// Effects
const fetchProfitCenters = createEffect<{ onlyUserProfitCenters?: boolean } | void, ProfitCenters, CustomError>(async (params): Promise<ProfitCenters> => {
    try {
        const dto: IGetProfitCentersDto = await ApiService.request({
            serviceName: ApiServiceName.asset,
            followLink: {
                entity: ApiLinkEntity.profitCenter,
                rel: ApiLinkRel.all,
                query: { onlyUserProfitCenters: params?.onlyUserProfitCenters },
            },
        })

        return new ProfitCenters(dto)
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

export const fetchTenantProfitCenters = createEffect(async (): Promise<ProfitCenters> => {
    return fetchProfitCenters()
})

export const fetchUserProfitCenters = createEffect(async (): Promise<ProfitCenters> => {
    return fetchProfitCenters({ onlyUserProfitCenters: true })
})

export const createProfitCenter = createEffect<{ link: IApiLink, data: ICreateProfitCenterDto }, void, CustomError>(async ({ link, data }): Promise<void> => {
    try {
        await ApiService.followLink(
            {
                link,
                data
            }
        )
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

// Helpers
const noProfitCenterFilter: IAppliedFilter = {
    value: null,
    category: FilterCategory.profitCenter
}

export function filterOutNotAllowedProfitCenterFilters(filters: IAppliedFilter[]) {
    const userProfitCenterIds = $userProfitCenterIds.getState()
    return filters.filter(filter => filter.category !== FilterCategory.profitCenter || userProfitCenterIds?.includes(filter.value))
}

export function addImplicitProfitCenterFiltersIfNeeded(filters: IAppliedFilter[]) {
    const userProfitCenters = $userProfitCenters.getState()
    const tenantProfitCenters = $tenantProfitCenters.getState()
    const hasProfitCenterFilters = filters.some(filter => filter.category === FilterCategory.profitCenter)
    if (hasProfitCenterFilters || !tenantProfitCenters || tenantProfitCenters.items.length === 0) {
        return filters
    }
    // if no profit center filters applied,
    // filter by all user allowed profit centers
    return [
        ...filters,
        ...(!userProfitCenters || userProfitCenters.items.length === 0)
            ? (tenantProfitCenters.items.length > 0 ? [noProfitCenterFilter] : [])
            : [
                ...userProfitCenters.items.map(pc => ({ value: pc.id, category: FilterCategory.profitCenter })),
                noProfitCenterFilter,
            ]
    ]
}

// Listeners
$tenantProfitCenters
    .on(fetchTenantProfitCenters.doneData, (_, payload) => payload)
$userProfitCenters
    .on(fetchUserProfitCenters.doneData, (_, payload) => payload)
